import Controller from '@ember/controller';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import HotelBudgetForecastTableDayCell from 'lumen/components/hotel/budget-forecast/table/day-cell';
import type { TableLayoutSettings } from 'lumen/components/hotel/budget-forecast/table/layout-settings';
import HotelBudgetForecastTableTdWithVariance from 'lumen/components/hotel/budget-forecast/table/td-with-variance';
import HotelBudgetForecastTableThActions from 'lumen/components/hotel/budget-forecast/table/th-actions';
import type {
  BudgetForecastCapacity,
  BudgetForecastDataItem,
  BudgetForecastEntry,
  BudgetForecastSegment,
  Day,
} from 'lumen/models';
import type { CapacityMeta } from 'lumen/models/budget-forecast/capacity';
import type {
  Meta,
  ResourceDataType,
} from 'lumen/models/budget-forecast/data-item';
import type HotelBudgetRoute from 'lumen/routes/hotel/budget';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  calculateVariance,
  groupItemsByDate,
} from 'lumen/utils/budget-forecast';
import { LayoutSettingsHandler } from 'lumen/utils/budget-forecast/layout-settings';
import { isWeekendDate } from 'lumen/utils/charts/chart-utils';
import {
  BUDGET_VIEW_DATA_TYPE_ORDER,
  COLUMN_PROPERTIES,
  DATA_TYPE_TRANSLATIONS,
  type DataType,
  DATE_GRANULARITY,
  type DateGranularity,
  DEFAULT_ENTRY_TITLE_TRANSLATIONS,
} from 'lumen/utils/constants/budget-forecast-constants';
import { type Variance, VARIANCES } from 'lumen/utils/constants/variance';
import type { ModelFrom } from 'lumen/utils/routes';

export type CustomExportOptions = {
  endDate: string;
  granularity: DateGranularity;
  startDate: string;
  view: 'budget' | 'forecast';
  visibleDataTypes: DataType[];
};

type CustomExportQueryParams = {
  date_aggregation: DateGranularity;
  end_date: string;
  entry_id: string;
  start_date: string;
  subscription_id: string;
  visible_data_types: string;
};

export default class HotelBudgetController extends Controller {
  declare model: ModelFrom<HotelBudgetRoute>;
  queryParams = ['granularity', 'startDate', 'endDate', 'variance', 'entryId'];

  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked dataTypes: DataType[] = ['budget', ...BUDGET_VIEW_DATA_TYPE_ORDER];
  @tracked endDate = dayjs().endOf('month').format('YYYY-MM-DD');
  @tracked entryId?: string;
  @tracked expandedRowIds: string[] = [];
  @tracked granularity: DateGranularity = DATE_GRANULARITY.DAY;
  @tracked layoutSettings: TableLayoutSettings = {
    hiddenDataTypes: [],
    pinnedDataTypes: [],
    unpinnedDataTypes: BUDGET_VIEW_DATA_TYPE_ORDER,
  };
  @tracked selectedExportType?: 'custom' | 'finance-system';

  @tracked startDate = dayjs().startOf('month').format('YYYY-MM-DD');
  @tracked variance: Variance = VARIANCES.NONE;

  #layoutSettingsHandler: LayoutSettingsHandler;
  baseDataType: ResourceDataType = 'budget';

  get capacities(): BudgetForecastCapacity[] {
    return this.model.capacities.last?.value?.capacities ?? [];
  }

  get capacityMetadata(): CapacityMeta {
    return (
      this.model.capacities.last?.value?.meta ?? {
        totals: {
          available_rooms: 0,
          physical_rooms: 0,
          out_of_order_rooms: 0,
        },
      }
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const { pinnedDataTypes } = this.layoutSettings;
    const { baseDataType, dataTypeOrder, expandedRowIds, visibleDataTypes } =
      this;

    const nameColumn = {
      name: '',
      valuePath: '',
      isFixed: 'left' as const,
      hasRightBorder: true,
      subcolumns: [
        {
          name: '',
          hasRightBorder: true,
          valuePath: COLUMN_PROPERTIES.NAME,
          isFixed: 'left' as const,
          width: 250,
          tdComponent: HotelBudgetForecastTableDayCell,
          thComponent: HotelBudgetForecastTableThActions,
          isCollapseDisabled: expandedRowIds.length === 0,
          collapseRows: (): void => {
            this.onCollapseAllRows();
          },
        },
      ],
    };

    const dataColumns = visibleDataTypes.map((dataType, i) => {
      const hasRightBorder = i !== dataTypeOrder.length - 1;

      return {
        name: this.intl.t(DATA_TYPE_TRANSLATIONS[dataType]),
        hasRightBorder,
        valuePath: '',
        isFixed:
          dataType === baseDataType ||
          (pinnedDataTypes ?? []).includes(dataType)
            ? ('left' as const)
            : undefined,
        subcolumns: [
          {
            name: this.intl.t('settings.budget_forecast.rooms.short'),
            valuePath: `${dataType}.${COLUMN_PROPERTIES.ROOMS}`,
            textAlign: 'right' as const,
            tdComponent: HotelBudgetForecastTableTdWithVariance,
          },
          {
            name: this.intl.t('settings.budget_forecast.adr.short'),
            valuePath: `${dataType}.${COLUMN_PROPERTIES.ADR}`,
            textAlign: 'right' as const,
            tdComponent: HotelBudgetForecastTableTdWithVariance,
          },
          {
            name: this.intl.t('settings.budget_forecast.revenue.short'),
            valuePath: `${dataType}.${COLUMN_PROPERTIES.REVENUE}`,
            textAlign: 'right' as const,
            tdComponent: HotelBudgetForecastTableTdWithVariance,
            hasRightBorder,
          },
        ],
      };
    });

    return [nameColumn, ...dataColumns];
  }

  get data(): BudgetForecastDataItem[] {
    return this.model.dataItems.last?.value?.dataItems ?? [];
  }

  get dataTypeOrder(): DataType[] {
    const { pinnedDataTypes, unpinnedDataTypes } = this.layoutSettings;
    const { baseDataType } = this;

    return [
      baseDataType,
      ...(pinnedDataTypes ?? []),
      ...(unpinnedDataTypes ?? []),
    ];
  }

  @cached // Preserve reference for isEqual check in PowerSelect code
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get entryOptions() {
    if (!this.model.entries.last?.value) {
      return [];
    }

    const sortedEntries = this.model.entries.last.value.toSorted((a, b) => {
      if (a.isMain) {
        return -1;
      }

      if (b.isMain) {
        return 1;
      }

      return a.titleWithDefault.localeCompare(b.titleWithDefault);
    });

    return sortedEntries.map((entry) => ({
      id: entry.id!,
      label: entry.titleWithDefault,
      isMain: entry.isMain,
      badge: entry.isMain ? this.intl.t('budget_forecast.main') : undefined,
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get exportChoices() {
    const choices: {
      groupName: string;
      options: {
        id: 'current-view' | 'custom' | 'finance-system';
        label: string;
      }[];
    }[] = [
      {
        groupName: this.intl.t('general.export_group.export'),
        options: [
          {
            id: 'current-view' as const,
            label: this.intl.t('budget_forecast.export.current_view'),
          },
          { id: 'custom' as const, label: this.intl.t('general.custom') },
        ],
      },
    ];

    if (this.access.hasBudgetAndForecastFinanceExport) {
      choices.push({
        groupName: this.intl.t('general.other'),
        options: [
          {
            id: 'finance-system' as const,
            label: this.intl.t('budget_forecast.export.send_to_finance_system'),
          },
        ],
      });
    }

    return choices;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get footerRows() {
    const { totals } = this;
    const totalRow = this.buildBaseRow(
      this.intl.t('settings.budget_forecast.total'),
      'total',
      null,
      false,
      [],
    );

    totalRow['className'] = 'table-row--footer';

    const availableInventory = this.capacityMetadata.totals.available_rooms;

    this.dataTypes.forEach((dataType) => {
      const item = totals[dataType] as BudgetForecastDataItem | undefined;
      const base = totals[this.baseDataType] as
        | BudgetForecastDataItem
        | undefined;
      const variances = calculateVariance(base, item, this.variance);
      const inventoryDifference =
        dataType === this.baseDataType && base
          ? base.rooms - availableInventory
          : null;

      totalRow[dataType] = this.buildDataItem(
        item,
        variances,
        inventoryDifference,
      );
    });
    return [totalRow];
  }

  get isEntrySelectionDisabled(): boolean {
    return (
      this.model.entries.isRunning || Boolean(this.model.entries.last?.isError)
    );
  }

  get isLoading(): boolean {
    return (
      this.model.entries.isRunning ||
      this.model.segments.isRunning ||
      this.model.dataItems.isRunning
    );
  }

  get metadata(): Meta {
    return this.model.dataItems.last?.value?.meta ?? { totals: {} };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    const isDayGranularity = this.granularity === DATE_GRANULARITY.DAY;
    const dataGroupedByDate = groupItemsByDate(this.data);

    return Object.entries(dataGroupedByDate)
      .sort(([dateA], [dateB]) => dayjs(dateA).diff(dayjs(dateB)))
      .map(([date, items], index) => {
        const rootItems = items.filter((item) => item.isRoot);
        const day = isDayGranularity
          ? this.store.peekRecord<Day>('day', date)
          : null;
        const children = this.buildSegmentRows(
          date,
          this.segments.filter((segment) => segment.parent === null),
          items,
        );

        const unmappedRootItems = items.filter(
          (item) => item.isUnmapped && item.parent && item.parent.isRoot,
        );

        if (unmappedRootItems.length > 0) {
          const unmappedRow = this.buildUnmappedSegmentRow(
            `${date}-unmapped`,
            unmappedRootItems,
          );
          children.push(unmappedRow);
        }

        const row = this.buildBaseRow(
          items[0]!.formattedDate,
          date,
          day,
          false,
          children,
        );

        const capacityForDate = this.capacities.find(
          (capacity) => capacity.date.format('YYYY-MM-DD') === date,
        );
        this.dataTypes.forEach((dataType) => {
          const item = rootItems.find((i) => i.dataType === dataType);
          const base = item?.belongsTo(this.baseDataType).value();
          const variances = calculateVariance(base, item, this.variance);
          const inventoryDifference =
            dataType === this.baseDataType && item && capacityForDate
              ? item.rooms - capacityForDate.availableRooms
              : null;
          row[dataType] = this.buildDataItem(
            item,
            variances,
            inventoryDifference,
          );
        });

        let className = '';

        if (isWeekendDate(dayjs(date)) && isDayGranularity) {
          className += ' table-row--weekend';
        }

        if (index === 0) {
          className += ' ic-product-tour-48864127-first-row';
        }

        row['className'] = className;

        return row;
      });
  }

  get segments(): BudgetForecastSegment[] {
    return this.model.segments.last?.value ?? [];
  }

  get selectedEntry(): BudgetForecastEntry | null {
    if (!this.model.entries.last?.value) {
      return null;
    }

    return (
      this.model.entries.last.value.find(
        (entry) => entry.id === this.entryId,
      ) ?? null
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedEntryOption() {
    if (this.isEntrySelectionDisabled) {
      return {
        id: '',
        label: this.intl.t(DEFAULT_ENTRY_TITLE_TRANSLATIONS[this.baseDataType]),
        isMain: false,
        badge: undefined,
      };
    }

    const selectedOption = this.entryId
      ? this.entryOptions.find((option) => option.id === this.entryId)
      : this.entryOptions.find((option) => option.isMain);

    return selectedOption;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totals() {
    const { totals } = this.metadata;

    return Object.entries(totals).reduce<Record<string, unknown>>(
      (acc, [key, item]) => {
        acc[key] = {
          ...item,
          dataType: key,
        };
        return acc;
      },
      {},
    );
  }

  get visibleDataTypes(): DataType[] {
    const { dataTypeOrder, baseDataType } = this;
    const { hiddenDataTypes } = this.layoutSettings;

    return dataTypeOrder.filter(
      (dataType) =>
        dataType === baseDataType || !hiddenDataTypes.includes(dataType),
    );
  }

  constructor(owner: Owner) {
    super(owner);
    this.#layoutSettingsHandler = new LayoutSettingsHandler(owner);
  }

  buildBaseRow(
    name: string,
    rowId: string,
    day: Day | null,
    hasWarning: boolean,
    children: Record<string, unknown>[],
  ): Record<string, unknown> {
    return {
      [COLUMN_PROPERTIES.NAME]: {
        value: name,
        day,
        hasWarning,
      },
      isCollapsed: !this.expandedRowIds.includes(rowId),
      id: rowId,
      children,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildDataItem(
    item: BudgetForecastDataItem | undefined,
    variances: Record<string, unknown>,
    inventoryDifference: number | null = null,
  ) {
    if (!item) {
      return {
        [COLUMN_PROPERTIES.ROOMS]: {
          value: null,
        },
        [COLUMN_PROPERTIES.ADR]: {
          value: null,
        },
        [COLUMN_PROPERTIES.REVENUE]: {
          value: null,
        },
      };
    }

    const varianceType =
      item.dataType === this.baseDataType ? VARIANCES.NONE : this.variance;

    return {
      [COLUMN_PROPERTIES.ROOMS]: {
        value: item.rooms,
        varianceValue: variances[COLUMN_PROPERTIES.ROOMS],
        varianceType,
        hasWarning: inventoryDifference !== null && inventoryDifference > 0,
        inventoryDifference,
      },
      [COLUMN_PROPERTIES.ADR]: {
        value: item.adr,
        varianceValue: variances[COLUMN_PROPERTIES.ADR],
        varianceType,
      },
      [COLUMN_PROPERTIES.REVENUE]: {
        value: item.revenue,
        varianceValue: variances[COLUMN_PROPERTIES.REVENUE],
        varianceType,
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildSegmentRows(
    rowId: string,
    segments: BudgetForecastSegment[],
    items: BudgetForecastDataItem[],
  ) {
    return segments.map((segment) => {
      const itemsInSegment = items.filter(
        (item) => item.segment?.id === segment.id,
      );

      const children = this.buildSegmentRows(rowId, segment.children, items);
      const unmappedChildren = items.filter(
        (item) =>
          item.isUnmapped &&
          item.parent &&
          item.parent.segment?.id === segment.id,
      );
      if (unmappedChildren.length > 0) {
        const unmappedChild = this.buildUnmappedSegmentRow(
          `${rowId}-${segment.id}-unmapped`,
          unmappedChildren,
        );
        children.push(unmappedChild);
      }

      const row = this.buildBaseRow(
        segment.name,
        `${rowId}-${segment.id}`,
        null,
        false,
        children,
      );

      this.dataTypes.forEach((dataType) => {
        const budgetForecastItem = itemsInSegment.find(
          (item) => item.dataType === dataType,
        );
        const base = budgetForecastItem?.belongsTo(this.baseDataType).value();
        const variances = calculateVariance(
          base,
          budgetForecastItem,
          this.variance,
        );
        row[dataType] = this.buildDataItem(budgetForecastItem, variances);
      });

      return row;
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildUnmappedSegmentRow(rowId: string, items: BudgetForecastDataItem[]) {
    const unmappedItems = items.filter(
      (item) => item.isUnmapped && item.parent,
    );
    const row = this.buildBaseRow(
      this.intl.t('general.unmapped'),
      rowId,
      null,
      true,
      [],
    );

    this.dataTypes.forEach((dataType) => {
      const budgetForecastItem = unmappedItems.find(
        (item) => item.dataType === dataType,
      );
      const base = budgetForecastItem?.belongsTo(this.baseDataType).value();
      const variances = calculateVariance(
        base,
        budgetForecastItem,
        this.variance,
      );
      row[dataType] = this.buildDataItem(budgetForecastItem, variances);
    });

    return row;
  }

  @action
  downloadCustomExport(options: CustomExportOptions): void {
    if (!this.entryId) {
      return;
    }

    const { startDate, endDate, granularity, view, visibleDataTypes } = options;

    const baseURL =
      view === 'budget'
        ? '/apigateway/v1/app/download/budgetforecast/budget/'
        : '/apigateway/v1/app/download/budgetforecast/forecast/';

    const params: CustomExportQueryParams = {
      subscription_id: this.hotelAndMarketService.hotel.id!,
      start_date: startDate,
      end_date: endDate,
      date_aggregation: granularity,
      visible_data_types: visibleDataTypes.join(','),
      entry_id: this.entryId,
    };

    const exportURL = formatUrl(baseURL, params, this.ajax.host);

    openUrl(exportURL);
  }

  @action
  handleDateRangeChange(
    granularity: DateGranularity,
    startDate: string,
    endDate: string,
  ): void {
    this.granularity = granularity;
    this.startDate = startDate;
    this.endDate = endDate;
  }

  @action
  handleExport(
    exportChoice: (typeof this.exportChoices)[number]['options'][number],
  ): void {
    if (exportChoice['id'] === 'current-view') {
      this.trackExportClicked();
      return this.downloadCustomExport({
        startDate: this.startDate,
        endDate: this.endDate,
        granularity: this.granularity,
        view: this.baseDataType,
        visibleDataTypes: this.visibleDataTypes,
      });
    }

    this.selectedExportType = exportChoice['id'];
  }

  @action
  onCloseExportModal(): void {
    this.selectedExportType = undefined;
  }

  @action
  onCollapseAllRows(): void {
    this.expandedRowIds = [];
  }

  @action
  onRowCollapseToggle(row: (typeof this.rows)[number]): void {
    const { id } = row;

    if (typeof id !== 'string') {
      return;
    }

    if (this.expandedRowIds.includes(id)) {
      this.expandedRowIds = this.expandedRowIds.filter((rowId) => rowId !== id);
    } else {
      this.expandedRowIds = [...this.expandedRowIds, id];
    }
  }

  @action
  redirectToSettingsPage(): void {
    this.usageAnalytics.track('Budget Page Viewed', {
      eventTrigger: 'settings-budget-clicked',
    });

    this.router.transitionTo('hotel.settings.budget');
  }

  @action
  trackExportClicked(): void {
    if (this.baseDataType === 'budget') {
      this.usageAnalytics.track('Budget Exported', {
        eventTrigger: 'export-clicked',
        exportType: 'Current View',
      });
    } else {
      this.usageAnalytics.track('Forecast Exported', {
        eventTrigger: 'export-clicked',
        exportType: 'Current View',
      });
    }
  }

  @action
  updateSelectedEntry(option: (typeof this.entryOptions)[number]): void {
    this.entryId = option.id;
  }

  @action
  updateTableLayoutSettings(layoutSettings: TableLayoutSettings): void {
    this.#layoutSettingsHandler.setLayoutSettings({
      view: this.baseDataType,
      mode: 'view',
      layoutSettings,
    });

    this.layoutSettings = layoutSettings;
  }
}
